import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Save, RotateCcw, Eye, EyeOff } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import AdminNav from '@/components/AdminNav';

const AdminTileSettings = ({ user, setUser }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isEs = (i18n.language || 'en').toLowerCase().startsWith('es');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tiles, setTiles] = useState([]);
  const [overrides, setOverrides] = useState({});

  const labels = isEs
    ? {
        title: 'Personalizar Nombres de Tarjetas',
        subtitle: 'Renombre u oculte cualquier tarjeta del panel de administrador. Los cambios se aplican a todos los usuarios de su empresa.',
        back: 'Volver al Panel',
        save: 'Guardar Cambios',
        resetAll: 'Restablecer Todo',
        resetRow: 'Restablecer',
        nameEn: 'Nombre (EN)',
        nameEs: 'Nombre (ES)',
        descEn: 'Descripción (EN)',
        descEs: 'Descripción (ES)',
        hidden: 'Oculta',
        visible: 'Visible',
        defaultBadge: 'Predeterminado',
        modifiedBadge: 'Personalizado',
        hide: 'Ocultar',
        show: 'Mostrar',
        savedToast: 'Configuración guardada',
        resetToast: 'Restablecido a los valores predeterminados',
        errorToast: 'Error al guardar la configuración',
        confirmReset: '¿Está seguro de que desea restablecer todos los nombres de tarjetas a sus valores predeterminados?',
        unsaved: 'Cambios sin guardar',
        defaultLabel: 'Valor predeterminado',
      }
    : {
        title: 'Customize Tile Names',
        subtitle: 'Rename or hide any admin dashboard tile. Changes apply to every user in your company.',
        back: 'Back to Dashboard',
        save: 'Save Changes',
        resetAll: 'Reset All',
        resetRow: 'Reset',
        nameEn: 'Name (EN)',
        nameEs: 'Name (ES)',
        descEn: 'Description (EN)',
        descEs: 'Description (ES)',
        hidden: 'Hidden',
        visible: 'Visible',
        defaultBadge: 'Default',
        modifiedBadge: 'Customized',
        hide: 'Hide',
        show: 'Show',
        savedToast: 'Tile settings saved',
        resetToast: 'Restored to defaults',
        errorToast: 'Failed to save tile settings',
        confirmReset: 'Are you sure you want to reset all tile names to their defaults?',
        unsaved: 'Unsaved changes',
        defaultLabel: 'Default',
      };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await axiosInstance.get('/admin/tile-config');
        if (cancelled) return;
        setTiles(data.tiles || []);
        setOverrides(data.overrides || {});
      } catch (e) {
        if (!cancelled) toast.error(isEs ? 'Error al cargar la configuración' : 'Failed to load tile settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isEs]);

  const updateField = (key, field, value) => {
    setOverrides((prev) => {
      const next = { ...prev, [key]: { ...(prev[key] || {}), [field]: value } };
      return next;
    });
  };

  const resetRow = (key) => {
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axiosInstance.put('/admin/tile-config', { overrides });
      setOverrides(data.overrides || {});
      toast.success(labels.savedToast);
    } catch (e) {
      toast.error(labels.errorToast);
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(labels.confirmReset)) return;
    setSaving(true);
    try {
      await axiosInstance.post('/admin/tile-config/reset');
      setOverrides({});
      toast.success(labels.resetToast);
    } catch (e) {
      toast.error(labels.errorToast);
    } finally {
      setSaving(false);
    }
  };

  // Group tiles by category for readability
  const grouped = tiles.reduce((acc, t) => {
    const cat = t.category || 'other';
    acc[cat] = acc[cat] || [];
    acc[cat].push(t);
    return acc;
  }, {});
  const categoryOrder = ['people', 'training', 'safety', 'ops', 'library', 'gc', 'other'];
  const categoryLabels = isEs
    ? {
        people: 'Personas',
        training: 'Capacitación',
        safety: 'Seguridad',
        ops: 'Operaciones',
        library: 'Biblioteca',
        gc: 'Contratistas Generales',
        other: 'Otro',
      }
    : {
        people: 'People',
        training: 'Training',
        safety: 'Safety',
        ops: 'Operations',
        library: 'Library',
        gc: 'General Contractors',
        other: 'Other',
      };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav user={user} setUser={setUser} />
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin')}
              data-testid="tile-settings-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              {labels.back}
            </Button>
            <h1 className="text-3xl font-bold mt-2">{labels.title}</h1>
            <p className="text-sm text-gray-600 mt-1 max-w-2xl">{labels.subtitle}</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={resetAll}
              disabled={saving || loading || Object.keys(overrides).length === 0}
              data-testid="tile-settings-reset-all-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              {labels.resetAll}
            </Button>
            <Button
              onClick={save}
              disabled={saving || loading}
              data-testid="tile-settings-save-btn"
            >
              <Save className="w-4 h-4 mr-1" />
              {saving ? '…' : labels.save}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-10 text-gray-500">…</div>
        ) : (
          categoryOrder
            .filter((cat) => grouped[cat])
            .map((cat) => (
              <Card key={cat} className="mb-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-gray-700">
                    {categoryLabels[cat] || cat}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 pt-2">
                  {grouped[cat].map((tile) => {
                    const ov = overrides[tile.key] || {};
                    const isCustomized =
                      Boolean(ov.nameEn) ||
                      Boolean(ov.nameEs) ||
                      Boolean(ov.descEn) ||
                      Boolean(ov.descEs) ||
                      Boolean(ov.hidden);
                    const isHidden = Boolean(ov.hidden);
                    return (
                      <div
                        key={tile.key}
                        className={`border rounded-lg p-3 ${isHidden ? 'bg-gray-100 opacity-80' : 'bg-white'}`}
                        data-testid={`tile-settings-row-${tile.key}`}
                      >
                        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">
                              {isEs ? tile.nameEs : tile.nameEn}
                            </span>
                            <code className="text-xs text-gray-500 bg-gray-100 px-1 py-0.5 rounded">
                              {tile.key}
                            </code>
                            {isCustomized ? (
                              <Badge className="bg-blue-600 text-white">{labels.modifiedBadge}</Badge>
                            ) : (
                              <Badge variant="outline">{labels.defaultBadge}</Badge>
                            )}
                            {isHidden && (
                              <Badge className="bg-gray-500 text-white">{labels.hidden}</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              {isHidden ? (
                                <EyeOff className="w-4 h-4 text-gray-500" />
                              ) : (
                                <Eye className="w-4 h-4 text-gray-500" />
                              )}
                              <Switch
                                checked={!isHidden}
                                onCheckedChange={(checked) =>
                                  updateField(tile.key, 'hidden', !checked)
                                }
                                data-testid={`tile-settings-visible-${tile.key}`}
                              />
                              <span className="text-xs text-gray-600">
                                {isHidden ? labels.hide : labels.show}
                              </span>
                            </div>
                            {isCustomized && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => resetRow(tile.key)}
                                data-testid={`tile-settings-reset-${tile.key}`}
                              >
                                <RotateCcw className="w-3 h-3 mr-1" />
                                {labels.resetRow}
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">
                              {labels.nameEn}
                              <span className="ml-2 text-gray-400">
                                ({labels.defaultLabel}: {tile.nameEn})
                              </span>
                            </label>
                            <Input
                              value={ov.nameEn || ''}
                              onChange={(e) => updateField(tile.key, 'nameEn', e.target.value)}
                              placeholder={tile.nameEn}
                              maxLength={120}
                              data-testid={`tile-settings-nameEn-${tile.key}`}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">
                              {labels.nameEs}
                              <span className="ml-2 text-gray-400">
                                ({labels.defaultLabel}: {tile.nameEs})
                              </span>
                            </label>
                            <Input
                              value={ov.nameEs || ''}
                              onChange={(e) => updateField(tile.key, 'nameEs', e.target.value)}
                              placeholder={tile.nameEs}
                              maxLength={120}
                              data-testid={`tile-settings-nameEs-${tile.key}`}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            ))
        )}
      </div>
    </div>
  );
};

export default AdminTileSettings;
